import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import { prefersReducedMotion } from './motion';

/**
 * The shared accessible on/off switch for every portal's Settings surface —
 * replaces Corpus's checkbox flag, Forms' aria-pressed button and Vetting's
 * lodgement radios. Optimistic + revert-on-error: it flips immediately, awaits
 * `onChange`, and rolls back on rejection (Sexton's law — reversible actions act
 * at once, no confirm). Self-styled with tokens + fallbacks like SignOutMenu.
 */
export interface ToggleProps {
  checked: boolean;
  /** May be async. On rejection the switch reverts to `checked`. */
  onChange: (next: boolean) => void | Promise<void>;
  label: string;
  description?: string;
  disabled?: boolean;
  /** External busy flag (portal-driven) OR-ed with the internal in-flight state. */
  pending?: boolean;
  /** Called if `onChange` rejects (the switch only reverts; the portal owns copy). */
  onError?: (err: unknown) => void;
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
  pending,
  onError,
}: ToggleProps): JSX.Element {
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const reduce = prefersReducedMotion();

  useEffect(() => {
    // The parent's authoritative value caught up → drop the optimistic overlay.
    if (pendingValue !== null && checked === pendingValue) setPendingValue(null);
  }, [checked, pendingValue]);

  const displayed = pendingValue ?? checked;
  const busy = inFlight || pending === true;
  const isDisabled = disabled === true || busy;

  const onClick = (): void => {
    if (isDisabled) return;
    const next = !displayed;
    setPendingValue(next);
    setInFlight(true);
    Promise.resolve(onChange(next))
      .catch((err: unknown) => {
        setPendingValue(null); // revert
        onError?.(err);
      })
      .finally(() => setInFlight(false));
  };

  const row: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-4, 16px)',
    padding: '10px 0',
  };
  const track: CSSProperties = {
    flexShrink: 0,
    width: 40,
    height: 24,
    borderRadius: 'var(--radius-pill, 999px)',
    border: 0,
    padding: 0,
    position: 'relative',
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled && !busy ? 0.55 : 1,
    background: displayed ? 'var(--color-accent, #1f7a4d)' : 'var(--color-border, #cfc9bd)',
    transition: reduce ? 'none' : 'background var(--duration-fast, 120ms) var(--ease-standard)',
  };
  const thumb: CSSProperties = {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
    transform: displayed ? 'translateX(16px)' : 'translateX(0)',
    transition: reduce ? 'none' : 'transform var(--duration-fast, 120ms) var(--ease-standard)',
  };

  return (
    <div style={row}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-ink, #111827)' }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: 12.5, color: 'var(--color-muted, #6b7280)', marginTop: 2 }}>
            {description}
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={displayed}
        aria-label={label}
        aria-busy={busy}
        disabled={isDisabled}
        onClick={onClick}
        style={track}
      >
        <span aria-hidden="true" style={thumb} />
      </button>
    </div>
  );
}
